/**
 * Dashboard data layer.
 *
 *   Widget → DataSourceAdapter → ArcGIS REST | GeoServer WFS → widget data
 *
 * Widgets never talk to a GIS service directly; they ask for a statistic, a
 * category breakdown, or records, and the adapter decides how to get it. Adding
 * a third service type later means adding one adapter here and nothing else.
 *
 * WHERE THE WORK HAPPENS
 *  - ArcGIS computes statistics server-side (`outStatistics`,
 *    `groupByFieldsForStatistics`, `returnCountOnly`), so only the answer
 *    travels over the wire.
 *  - GeoServer WFS has no statistics endpoint, so records are fetched and
 *    aggregated in the browser. That read is capped (RECORD_LIMIT) and the
 *    result is flagged `truncated` so widgets can say the number is partial
 *    rather than quietly showing a wrong total.
 */

import type { LayerGeometryType } from '../../types';
import { getArcgisBaseUrl, getArcgisExtentBounds, withArcgisToken } from '../../utils/arcgis';
import { detectWfsGeometryType, fetchWfsFields } from '../../layers/sld';
import { aggregate, groupAndAggregate, sortCategories } from '../utils/dashboardUtils';
import type {
  DashboardDataSource,
  DashboardField,
  DashboardServiceType,
  StatisticType,
} from '../types/dashboardTypes';

/**
 * Maximum records pulled when a statistic must be computed in the browser.
 * High enough for typical utility-network layers, low enough not to lock up
 * the tab on a national dataset.
 */
export const RECORD_LIMIT = 5000;

export interface StatisticResult {
  value: number | null;
  /** True when computed from a capped record read rather than the full layer. */
  truncated: boolean;
}

export interface CategoryDatum {
  category: string;
  value: number;
}

export interface CategoryResult {
  data: CategoryDatum[];
  truncated: boolean;
}

export interface RecordResult {
  records: Record<string, unknown>[];
  /** Total matching the filter, when the service reports it. */
  total: number | null;
}

/** [minLon, minLat, maxLon, maxLat] in WGS84. */
export type Extent = [number, number, number, number];

export interface StatisticQuery {
  statistic: StatisticType;
  field?: string;
  where?: string;
  /** Spatial constraint contributed by a map widget's action. */
  extent?: Extent;
  /** Shape contributed by a spatially-matched selection action. */
  geometry?: GeoJSON.Geometry;
}

export interface CategoryQuery {
  categoryField: string;
  statistic: StatisticType;
  field?: string;
  where?: string;
  limit?: number;
  sortBy?: string;
  extent?: Extent;
  geometry?: GeoJSON.Geometry;
}

export interface RecordQuery {
  columns?: string[];
  where?: string;
  limit?: number;
  orderByField?: string;
  orderDirection?: 'asc' | 'desc';
  extent?: Extent;
  geometry?: GeoJSON.Geometry;
}

export interface DataSourceAdapter {
  fetchFields(source: DashboardDataSource): Promise<DashboardField[]>;
  fetchStatistic(source: DashboardDataSource, query: StatisticQuery): Promise<StatisticResult>;
  fetchCategories(source: DashboardDataSource, query: CategoryQuery): Promise<CategoryResult>;
  fetchRecords(source: DashboardDataSource, query: RecordQuery): Promise<RecordResult>;
  /** Distinct values for a field, offered as filter-value suggestions. */
  fetchDistinctValues(source: DashboardDataSource, field: string): Promise<string[]>;
}

/** A layer offered by a service during the "Connect" step. */
export interface DiscoveredLayer {
  /** ArcGIS numeric layer id, or the WMS/WFS typeName. */
  id: string;
  name: string;
  title: string;
  geometryType: LayerGeometryType;
  arcgisLayerId?: number;
}

import { localAdapter, localMatchingGeometry, localSourceExtent } from './localSource';

/* ────────────────────────────── shared helpers ──────────────────────────── */

/**
 * Past this many characters, the request goes out as a POST.
 *
 * A spatial filter carries a real shape, and a selection of any size becomes a
 * very long parameter: five US states merged come to about 70 KB of ring
 * coordinates even after rounding. Measured against a live ArcGIS service, a
 * 142 KB GET comes back `414 Request-URI Too Large` — and the app would report
 * that as "the service failed" when nothing is wrong with the service.
 *
 * 2,000 is well under the ~4–8 KB where servers and proxies start refusing,
 * and comfortably above every ordinary request, which stays a GET and stays
 * cacheable.
 */
const MAX_GET_URL = 2000;

/**
 * One JSON request, as a GET unless it is too long to be one.
 *
 * Both backends accept the identical parameters form-encoded in a POST body —
 * ArcGIS REST by design, GeoServer through its KVP interface — so the switch
 * needs no per-caller knowledge and every call site gets it at once.
 */
/**
 * `tokenSource`: the raw ArcGIS URL a request's `?token=` (if any) is read
 * from — see `withArcgisToken`. Optional because this same helper also
 * fetches plain GeoServer WFS URLs, which never carry one.
 */
async function fetchJson(url: string, tokenSource?: string): Promise<any> {
  let response: Response;
  const authedUrl = tokenSource ? withArcgisToken(url, tokenSource) : url;

  if (authedUrl.length > MAX_GET_URL && authedUrl.includes('?')) {
    const split = authedUrl.indexOf('?');
    response = await fetch(authedUrl.slice(0, split), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: authedUrl.slice(split + 1),
    });
  } else {
    response = await fetch(authedUrl);
  }

  if (!response.ok) throw new Error(`Service returned ${response.status}`);
  const data = await response.json();
  // ArcGIS reports failures as HTTP 200 with an `error` object in the body.
  if (data?.error) throw new Error(data.error.message || 'Service returned an error');
  return data;
}

/** Normalises an Esri field type to the three families widgets care about. */
function normaliseEsriFieldType(esriType: string): DashboardField['type'] {
  if (/Integer|Double|Single|SmallInteger|OID/i.test(esriType)) return 'number';
  if (/Date/i.test(esriType)) return 'date';
  if (/String|GUID|GlobalID/i.test(esriType)) return 'string';
  return 'other';
}

/** Same for an XSD type reported by WFS DescribeFeatureType. */
function normaliseXsdFieldType(xsdType: string): DashboardField['type'] {
  if (/int|long|short|double|decimal|float|number/i.test(xsdType)) return 'number';
  if (/date|time/i.test(xsdType)) return 'date';
  if (/string/i.test(xsdType)) return 'string';
  return 'other';
}

/** Builds the GeoServer WFS endpoint from a WMS or bare service URL. */
function getWfsUrl(serviceUrl: string): URL {
  const url = new URL(serviceUrl.trim());
  const path = url.pathname.replace(/\/+$/, '');
  url.pathname = /\/(wms|wfs)$/i.test(path)
    ? path.replace(/\/(wms|wfs)$/i, '/wfs')
    : `${path}/wfs`;
  url.search = '';
  return url;
}

/* ──────────────────────────────── ArcGIS ────────────────────────────────── */

function arcgisLayerUrl(source: DashboardDataSource): string {
  const base = getArcgisBaseUrl(source.serviceUrl);
  if (!base || source.arcgisLayerId === undefined) {
    throw new Error('ArcGIS data source is missing a service URL or layer id');
  }
  return `${base}/${source.arcgisLayerId}`;
}

/**
 * The layer's object-id column, cached per layer.
 *
 * `outStatistics` has no COUNT(*): it counts NON-NULL values of whichever
 * column it is given. Counting the group column itself therefore reports 0 for
 * the null group — the "(blank) 0" row that made a category with real records
 * look empty. The object id is never null, so counting it gives the row count
 * every group actually has.
 */
const objectIdFieldCache = new Map<string, string | null>();

async function arcgisObjectIdField(source: DashboardDataSource): Promise<string | null> {
  const url = arcgisLayerUrl(source);
  const cached = objectIdFieldCache.get(url);
  if (cached !== undefined) return cached;

  let resolved: string | null = null;
  try {
    const data = await fetchJson(`${url}?f=json`, source.serviceUrl);
    resolved = typeof data?.objectIdField === 'string' ? data.objectIdField : null;
  } catch (error) {
    console.warn('[dashboard] could not resolve the ArcGIS object-id field', error);
  }

  objectIdFieldCache.set(url, resolved);
  return resolved;
}

const arcgisAdapter: DataSourceAdapter = {
  async fetchFields(source) {
    const data = await fetchJson(`${arcgisLayerUrl(source)}?f=json`, source.serviceUrl);
    return (data?.fields ?? [])
      // Geometry and shape-length/area columns are not useful as indicators.
      .filter((field: any) => field?.name && !/^(shape|geometry)$/i.test(field.type ?? ''))
      .map((field: any) => ({
        name: field.name,
        alias: field.alias || field.name,
        type: normaliseEsriFieldType(String(field.type ?? '')),
        domain: Array.isArray(field.domain?.codedValues)
          ? field.domain.codedValues.map((entry: any) => ({
              code: entry.code,
              label: entry.name,
            }))
          : undefined,
      }));
  },

  async fetchStatistic(source, query) {
    const url = arcgisLayerUrl(source);
    const where = query.where?.trim() || '1=1';

    // Count has a dedicated, cheap endpoint.
    if (query.statistic === 'count') {
      const params = new URLSearchParams({
        where,
        returnCountOnly: 'true',
        f: 'json',
        ...esriExtentParams(query.extent, query.geometry),
      });
      const data = await fetchJson(`${url}/query?${params.toString()}`, source.serviceUrl);
      return { value: Number(data?.count ?? 0), truncated: false };
    }

    if (!query.field) return { value: null, truncated: false };

    const outStatistics = JSON.stringify([
      {
        statisticType: query.statistic === 'avg' ? 'avg' : query.statistic,
        onStatisticField: query.field,
        outStatisticFieldName: 'value',
      },
    ]);

    try {
      const params = new URLSearchParams({
        where,
        outStatistics,
        f: 'json',
        ...esriExtentParams(query.extent, query.geometry),
      });
      const data = await fetchJson(`${url}/query?${params.toString()}`, source.serviceUrl);
      const attributes = data?.features?.[0]?.attributes ?? {};
      // Esri echoes the requested alias, but casing varies between versions.
      const raw = attributes.value ?? attributes.VALUE ?? Object.values(attributes)[0];
      const value = raw === null || raw === undefined ? null : Number(raw);
      return { value: Number.isFinite(value as number) ? (value as number) : null, truncated: false };
    } catch (error) {
      // Some older MapServer layers reject outStatistics; fall back to reading
      // the column and aggregating here rather than showing an error.
      console.warn('[dashboard] ArcGIS outStatistics failed, using client aggregation', error);
      const fallback = await arcgisAdapter.fetchRecords(source, {
        columns: [query.field],
        where: query.where,
        limit: RECORD_LIMIT,
      });
      return {
        value: aggregate(
          fallback.records.map((record) => record[query.field as string]),
          query.statistic,
        ),
        truncated: fallback.records.length >= RECORD_LIMIT,
      };
    }
  },

  async fetchCategories(source, query) {
    const url = arcgisLayerUrl(source);
    const where = query.where?.trim() || '1=1';
    const statisticField = query.statistic === 'count' ? query.categoryField : query.field;

    if (query.statistic !== 'count' && !statisticField) {
      return { data: [], truncated: false };
    }

    try {
      const countField =
        query.statistic === 'count' ? await arcgisObjectIdField(source) : null;
      const outStatistics = JSON.stringify([
        {
          statisticType: query.statistic === 'count' ? 'count' : query.statistic,
          // See arcgisObjectIdField: counting the group column loses null groups.
          onStatisticField: countField ?? statisticField,
          outStatisticFieldName: 'value',
        },
      ]);
      const params = new URLSearchParams({
        where,
        ...esriExtentParams(query.extent, query.geometry),
        groupByFieldsForStatistics: query.categoryField,
        outStatistics,
        f: 'json',
      });
      const data = await fetchJson(`${url}/query?${params.toString()}`, source.serviceUrl);

      const rows: CategoryDatum[] = (data?.features ?? []).map((feature: any) => {
        const attributes = feature?.attributes ?? {};
        const rawCategory = attributes[query.categoryField];
        const rawValue = attributes.value ?? attributes.VALUE;
        return {
          category:
            rawCategory === null || rawCategory === undefined || String(rawCategory).trim() === ''
              ? '(blank)'
              : String(rawCategory).trim(),
          value: Number(rawValue ?? 0),
        };
      });

      const sorted = sortCategories(rows, query.sortBy);
      return { data: sorted.slice(0, query.limit ?? 10), truncated: false };
    } catch (error) {
      console.warn('[dashboard] ArcGIS groupBy failed, using client aggregation', error);
      const columns = [query.categoryField, ...(query.field ? [query.field] : [])];
      const fallback = await arcgisAdapter.fetchRecords(source, {
        columns,
        where: query.where,
        limit: RECORD_LIMIT,
      });
      const grouped = groupAndAggregate(
        fallback.records,
        query.categoryField,
        query.statistic,
        query.field,
      );
      return {
        data: sortCategories(grouped, query.sortBy).slice(0, query.limit ?? 10),
        truncated: fallback.records.length >= RECORD_LIMIT,
      };
    }
  },

  async fetchRecords(source, query) {
    const url = arcgisLayerUrl(source);
    const params = new URLSearchParams({
      where: query.where?.trim() || '1=1',
      ...esriExtentParams(query.extent, query.geometry),
      outFields: query.columns?.length ? query.columns.join(',') : '*',
      returnGeometry: 'false',
      resultRecordCount: String(query.limit ?? 50),
      f: 'json',
    });
    if (query.orderByField) {
      params.set(
        'orderByFields',
        `${query.orderByField} ${query.orderDirection === 'desc' ? 'DESC' : 'ASC'}`,
      );
    }

    const data = await fetchJson(`${url}/query?${params.toString()}`, source.serviceUrl);
    const records = (data?.features ?? []).map((feature: any) => feature?.attributes ?? {});

    // A separate count call keeps "showing 50 of 2,543" honest.
    let total: number | null = null;
    try {
      const countParams = new URLSearchParams({
        where: query.where?.trim() || '1=1',
        // Same extent as the page above, or "50 of 2,543" would count rows the
        // table is not allowed to show.
        ...esriExtentParams(query.extent, query.geometry),
        returnCountOnly: 'true',
        f: 'json',
      });
      const countData = await fetchJson(`${url}/query?${countParams.toString()}`, source.serviceUrl);
      total = Number(countData?.count ?? 0);
    } catch {
      total = null;
    }

    return { records, total };
  },

  async fetchDistinctValues(source, field) {
    const url = arcgisLayerUrl(source);
    try {
      const params = new URLSearchParams({
        where: '1=1',
        outFields: field,
        returnDistinctValues: 'true',
        returnGeometry: 'false',
        resultRecordCount: '200',
        f: 'json',
      });
      const data = await fetchJson(`${url}/query?${params.toString()}`, source.serviceUrl);
      const values = (data?.features ?? [])
        .map((feature: any) => feature?.attributes?.[field])
        .filter((value: unknown) => value !== null && value !== undefined && String(value).trim() !== '')
        .map((value: unknown) => String(value).trim());
      return Array.from(new Set<string>(values)).slice(0, 200);
    } catch {
      return [];
    }
  },
};

/**
 * Extra ArcGIS query parameters for a spatial constraint.
 *
 * An envelope is expressed as a real geometry filter rather than folded into
 * the `where` string: `where` is attribute SQL and has no way to say "inside
 * this box", and the service can use its spatial index for this.
 */
function esriExtentParams(extent?: Extent, geometry?: GeoJSON.Geometry): Record<string, string> {
  /* A shape wins over an envelope. Both are "restrict to here", and a service
     takes only one geometry per query — the shape is the more specific of the
     two, so it is the one worth sending. */
  if (geometry) {
    const shape = esriGeometryFor(geometry);
    if (shape) {
      return {
        geometry: JSON.stringify({ ...shape.geometry, spatialReference: { wkid: 4326 } }),
        geometryType: shape.type,
        inSR: '4326',
        spatialRel: 'esriSpatialRelIntersects',
      };
    }
  }
  if (!extent) return {};
  return {
    geometry: extent.join(','),
    geometryType: 'esriGeometryEnvelope',
    inSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
  };
}

/**
 * Coordinate decimals kept when a shape is sent to a service.
 *
 * Six is about 11 cm at the equator — finer than any boundary someone selects
 * by, and far finer than the data itself is usually surveyed. Services hand
 * back full float precision (17 significant digits), which is 40% more
 * characters for accuracy that cannot matter to an INTERSECTS test.
 */
const FILTER_COORD_DECIMALS = 6;

/** A geometry with its coordinates rounded, whatever its nesting depth. */
function roundGeometry<T extends GeoJSON.Geometry>(geometry: T): T {
  const trim = (value: any): any =>
    Array.isArray(value)
      ? value.map(trim)
      : typeof value === 'number'
        ? Number(value.toFixed(FILTER_COORD_DECIMALS))
        : value;
  return { ...geometry, coordinates: trim((geometry as any).coordinates) };
}

/**
 * A GeoJSON geometry in the shape ArcGIS expects, with its type name.
 *
 * Esri names its geometries by dimension and carries the coordinates under a
 * different key for each — `x`/`y`, `points`, `paths`, `rings` — so the type
 * and the payload have to be chosen together or the service reads the geometry
 * as empty and matches nothing.
 */
function esriGeometryFor(
  input: GeoJSON.Geometry,
): { type: string; geometry: Record<string, unknown> } | null {
  const geometry = roundGeometry(input);
  const coordinates = (geometry as any).coordinates;
  if (!Array.isArray(coordinates) || coordinates.length === 0) return null;

  switch (geometry.type) {
    case 'Point': {
      const [x, y] = coordinates as number[];
      return { type: 'esriGeometryPoint', geometry: { x, y } };
    }
    case 'MultiPoint':
      return { type: 'esriGeometryMultipoint', geometry: { points: coordinates } };
    case 'LineString':
      return { type: 'esriGeometryPolyline', geometry: { paths: [coordinates] } };
    case 'MultiLineString':
      return { type: 'esriGeometryPolyline', geometry: { paths: coordinates } };
    case 'Polygon':
    case 'MultiPolygon': {
      const rings = esriRingsFor(geometry);
      return rings ? { type: 'esriGeometryPolygon', geometry: { rings } } : null;
    }
    default:
      return null;
  }
}

/**
 * GeoJSON rings as Esri rings.
 *
 * The two formats differ in winding: GeoJSON wants an outer ring
 * counter-clockwise, Esri wants it CLOCKWISE, and a ring wound the wrong way
 * is meant to be read as a hole. Signed area decides which way each ring
 * currently runs, and flips the ones that disagree.
 *
 * Tested against sampleserver6, which normalises rings itself — both windings
 * gave identical counts there, hole included. So this is insurance rather than
 * the thing holding the query up: the spec puts the burden on the client, and
 * older ArcGIS Servers and the non-Esri services that speak this dialect do
 * not all clean up after one.
 */
function esriRingsFor(geometry: GeoJSON.Geometry): number[][][] | null {
  const polygons: number[][][][] =
    geometry.type === 'Polygon'
      ? [geometry.coordinates as number[][][]]
      : geometry.type === 'MultiPolygon'
        ? (geometry.coordinates as number[][][][])
        : [];
  if (polygons.length === 0) return null;

  const signedArea = (ring: number[][]) => {
    let sum = 0;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
      sum += (ring[i][0] - ring[j][0]) * (ring[i][1] + ring[j][1]);
    }
    return sum;
  };

  const rings: number[][][] = [];
  for (const polygon of polygons) {
    polygon.forEach((ring, index) => {
      const clockwise = signedArea(ring) > 0;
      // Outer rings clockwise, holes counter-clockwise.
      const wantClockwise = index === 0;
      rings.push(clockwise === wantClockwise ? ring : [...ring].reverse());
    });
  }
  return rings;
}

/* ────────────────────────────── GeoServer ───────────────────────────────── */

/**
 * Name of the geometry column, cached per layer.
 *
 * `fetchFields` deliberately drops gml: columns because they are not something
 * a user can chart, but a CQL BBOX filter has to name the geometry column
 * explicitly — GeoServer has no "whatever the geometry is" keyword. Resolved
 * once per layer and remembered, since it never changes.
 */
const geometryFieldCache = new Map<string, string | null>();

async function wfsGeometryField(source: DashboardDataSource): Promise<string | null> {
  const key = `${source.serviceUrl}|${source.layerName}`;
  const cached = geometryFieldCache.get(key);
  if (cached !== undefined) return cached;

  let resolved: string | null = null;
  try {
    const url = getWfsUrl(source.serviceUrl);
    url.searchParams.set('service', 'WFS');
    url.searchParams.set('version', '1.1.0');
    url.searchParams.set('request', 'DescribeFeatureType');
    url.searchParams.set('typeName', source.layerName);

    const response = await fetch(url.toString());
    if (response.ok) {
      const xml = new DOMParser().parseFromString(await response.text(), 'application/xml');
      const elements = Array.from(xml.getElementsByTagName('xsd:element')).concat(
        Array.from(xml.getElementsByTagName('element')),
      );
      const geometry = elements.find((node) => /^gml:/i.test(node.getAttribute('type') ?? ''));
      resolved = geometry?.getAttribute('name') ?? null;
    }
  } catch (error) {
    console.warn('[dashboard] could not resolve the WFS geometry column', error);
  }

  geometryFieldCache.set(key, resolved);
  return resolved;
}

/** Joins CQL fragments, ignoring empties. */
function andCql(...parts: (string | undefined | null)[]): string {
  const kept = parts.map((part) => part?.trim()).filter((part): part is string => Boolean(part));
  if (kept.length === 0) return '';
  return kept.length === 1 ? kept[0] : kept.map((part) => `(${part})`).join(' AND ');
}

/** Runs a WFS GetFeature and returns the plain property records. */
async function wfsGetFeature(
  source: DashboardDataSource,
  options: {
    columns?: string[];
    where?: string;
    limit?: number;
    sortBy?: string;
    extent?: Extent;
    /** Shape from a spatially-matched action; see cqlWithExtent. */
    geometry?: GeoJSON.Geometry;
  },
): Promise<Record<string, unknown>[]> {
  const url = getWfsUrl(source.serviceUrl);
  url.searchParams.set('service', 'WFS');
  url.searchParams.set('version', '2.0.0');
  url.searchParams.set('request', 'GetFeature');
  url.searchParams.set('typeNames', source.layerName);
  url.searchParams.set('outputFormat', 'application/json');
  url.searchParams.set('count', String(options.limit ?? RECORD_LIMIT));
  if (options.columns?.length) url.searchParams.set('propertyName', options.columns.join(','));

  /* The spatial constraint rides in the SAME CQL_FILTER as the attribute one:
     GeoServer honours either CQL_FILTER or the separate `bbox` parameter, never
     both in one request, so combining them here avoids the filter being
     silently dropped. */
  const cql = await cqlWithExtent(source, options.where, options.extent, options.geometry);
  if (cql) url.searchParams.set('CQL_FILTER', cql);
  if (options.sortBy) url.searchParams.set('sortBy', options.sortBy);

  const data = await fetchJson(url.toString());
  return (data?.features ?? []).map((feature: any) => feature?.properties ?? {});
}

/**
 * An attribute filter and a map extent, combined into one CQL expression.
 *
 * Shared by every WFS request that can take an extent, because the bug this
 * replaces was one path FORGETTING it: the `resultType=hits` count set
 * CQL_FILTER from `where` alone, so a gauge counting features reported the
 * whole layer no matter where the driving map was panned. A count is exactly
 * the widget most likely to be driven by a map extent, and it was the one path
 * that ignored it.
 *
 * The CRS argument is not optional either. Under WFS 2.0.0 the URN form of
 * EPSG:4326 has LATITUDE first, so a four-argument BBOX built from the map's
 * own extent — west, south, east, north — is read as lat, lon, lat, lon: a box in
 * the wrong hemisphere, matching nothing, for every extent equally. Naming
 * `EPSG:4326` pins the longitude-first reading the numbers actually carry.
 */
async function cqlWithExtent(
  source: DashboardDataSource,
  where?: string,
  extent?: Extent,
  geometry?: GeoJSON.Geometry,
): Promise<string> {
  let cql = where?.trim() ?? '';
  if (!extent && !geometry) return cql;

  const geometryField = await wfsGeometryField(source);
  if (!geometryField) {
    console.warn(
      `[dashboard] no geometry column found for "${source.layerName}"; the spatial filter was skipped.`,
    );
    return cql;
  }

  /* A shape wins over an envelope, for the same reason it does on the ArcGIS
     side: both say "restrict to here" and the shape says it more precisely. */
  const wkt = geometry ? wktFor(geometry) : null;
  if (wkt) return andCql(cql, `INTERSECTS(${geometryField},${wkt})`);

  if (!extent) return cql;
  const [minX, minY, maxX, maxY] = extent;
  return andCql(cql, `BBOX(${geometryField},${minX},${minY},${maxX},${maxY},'EPSG:4326')`);
}

/**
 * A geometry as WKT, for CQL's INTERSECTS.
 *
 * Every type, not just polygons: a source layer of valves or mains is a
 * perfectly ordinary thing to filter by, and returning null for those made the
 * spatial filter vanish without a word — the target simply showed everything,
 * looking for all the world like a working query.
 *
 * WKT is longitude-first everywhere, with no CRS argument to disagree about —
 * which is why the axis-order trap that bites BBOX does not exist here.
 */
function wktFor(input: GeoJSON.Geometry): string | null {
  const geometry = roundGeometry(input);
  const point = ([x, y]: number[]) => `${x} ${y}`;
  const list = (coords: number[][]) => `(${coords.map(point).join(', ')})`;
  const nest = (rings: number[][][]) => `(${rings.map(list).join(', ')})`;
  const coordinates = (geometry as any).coordinates;
  if (!Array.isArray(coordinates) || coordinates.length === 0) return null;

  switch (geometry.type) {
    case 'Point':
      return `POINT(${point(coordinates as number[])})`;
    case 'MultiPoint':
      // Each point parenthesised on its own: the bare comma-separated form a
      // LINESTRING uses is not valid here, and parsers reject the whole filter.
      return `MULTIPOINT(${(coordinates as number[][]).map((p) => `(${point(p)})`).join(', ')})`;
    case 'LineString':
      return `LINESTRING${list(coordinates as number[][])}`;
    case 'MultiLineString':
      return `MULTILINESTRING(${(coordinates as number[][][]).map(list).join(', ')})`;
    case 'Polygon':
      return `POLYGON${nest(coordinates as number[][][])}`;
    case 'MultiPolygon':
      return `MULTIPOLYGON(${(coordinates as number[][][][]).map(nest).join(', ')})`;
    default:
      return null;
  }
}

const geoserverAdapter: DataSourceAdapter = {
  async fetchFields(source) {
    // DescribeFeatureType is the only place WFS publishes column types.
    const url = getWfsUrl(source.serviceUrl);
    url.searchParams.set('service', 'WFS');
    url.searchParams.set('version', '1.1.0');
    url.searchParams.set('request', 'DescribeFeatureType');
    url.searchParams.set('typeName', source.layerName);

    try {
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`WFS returned ${response.status}`);
      const xml = new DOMParser().parseFromString(await response.text(), 'application/xml');

      const elements = Array.from(xml.getElementsByTagName('xsd:element')).concat(
        Array.from(xml.getElementsByTagName('element')),
      );

      const fields = elements
        .map((node) => ({
          name: node.getAttribute('name') ?? '',
          rawType: node.getAttribute('type') ?? '',
        }))
        .filter((field) => field.name && !/^gml:/i.test(field.rawType))
        .map((field) => ({
          name: field.name,
          alias: field.name,
          type: normaliseXsdFieldType(field.rawType),
        }));

      if (fields.length > 0) return fields;
    } catch (error) {
      console.warn('[dashboard] WFS DescribeFeatureType failed, inferring fields', error);
    }

    // Fallback: infer the columns (and their types) from one sample record.
    try {
      const sample = await wfsGetFeature(source, { limit: 1 });
      const record = sample[0] ?? {};
      return Object.entries(record).map(([name, value]) => ({
        name,
        alias: name,
        type: typeof value === 'number' ? 'number' : 'string',
      }));
    } catch {
      // Last resort — reuse the symbology field reader, which returns names only.
      const names = await fetchWfsFields(source.serviceUrl, source.layerName);
      return names.map((field) => ({ name: field.name, alias: field.alias, type: 'other' as const }));
    }
  },

  async fetchStatistic(source, query) {
    // `resultType=hits` gives an exact count without transferring features.
    if (query.statistic === 'count') {
      const url = getWfsUrl(source.serviceUrl);
      url.searchParams.set('service', 'WFS');
      url.searchParams.set('version', '2.0.0');
      url.searchParams.set('request', 'GetFeature');
      url.searchParams.set('typeNames', source.layerName);
      url.searchParams.set('resultType', 'hits');
      url.searchParams.set('outputFormat', 'application/json');
      // The extent travels with the attribute filter — see cqlWithExtent.
      const cql = await cqlWithExtent(source, query.where, query.extent, query.geometry);
      if (cql) url.searchParams.set('CQL_FILTER', cql);

      try {
        const data = await fetchJson(url.toString());
        const matched = data?.numberMatched ?? data?.totalFeatures;
        const value = Number(matched);
        if (Number.isFinite(value)) return { value, truncated: false };
      } catch (error) {
        console.warn('[dashboard] WFS hits count failed, counting records', error);
      }
    }

    if (query.statistic !== 'count' && !query.field) return { value: null, truncated: false };

    const columns = query.statistic === 'count' ? undefined : [query.field as string];
    const records = await wfsGetFeature(source, {
      columns,
      where: query.where,
      extent: query.extent,
      geometry: query.geometry,
      limit: RECORD_LIMIT,
    });

    return {
      value: aggregate(
        records.map((record) => (query.field ? record[query.field] : record)),
        query.statistic,
      ),
      truncated: records.length >= RECORD_LIMIT,
    };
  },

  async fetchCategories(source, query) {
    const columns = [query.categoryField, ...(query.field ? [query.field] : [])];
    const records = await wfsGetFeature(source, {
      columns,
      where: query.where,
      extent: query.extent,
      geometry: query.geometry,
      limit: RECORD_LIMIT,
    });

    const grouped = groupAndAggregate(
      records,
      query.categoryField,
      query.statistic,
      query.field,
    );

    return {
      data: sortCategories(grouped, query.sortBy).slice(0, query.limit ?? 10),
      truncated: records.length >= RECORD_LIMIT,
    };
  },

  async fetchRecords(source, query) {
    const records = await wfsGetFeature(source, {
      columns: query.columns,
      where: query.where,
      extent: query.extent,
      geometry: query.geometry,
      limit: query.limit ?? 50,
      sortBy: query.orderByField
        ? `${query.orderByField} ${query.orderDirection === 'desc' ? 'D' : 'A'}`
        : undefined,
    });

    let total: number | null = null;
    try {
      const count = await geoserverAdapter.fetchStatistic(source, {
        statistic: 'count',
        where: query.where,
        // Must match the page above, or the "of N" is counting hidden rows.
        extent: query.extent,
        geometry: query.geometry,
      });
      total = count.value;
    } catch {
      total = null;
    }

    return { records, total };
  },

  async fetchDistinctValues(source, field) {
    // WFS has no DISTINCT; de-duplicate a capped read instead.
    try {
      const records = await wfsGetFeature(source, { columns: [field], limit: 1000 });
      const values = records
        .map((record) => record[field])
        .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
        .map((value) => String(value).trim());
      return Array.from(new Set(values)).slice(0, 200);
    } catch {
      return [];
    }
  },
};

/* ─────────────────────────────── Public API ─────────────────────────────── */

export function getDataSourceAdapter(source: DashboardDataSource): DataSourceAdapter {
  // An imported file answers its own queries — see services/localSource.ts.
  if (source.serviceType === 'local') return localAdapter;
  return source.serviceType === 'geoserver' ? geoserverAdapter : arcgisAdapter;
}

/**
 * Lists the layers a service publishes, for the "Connect" step.
 * Mirrors the discovery the Map App builder performs, so the same URLs work in
 * both application types.
 */
export async function discoverLayers(
  serviceType: DashboardServiceType,
  serviceUrl: string,
): Promise<DiscoveredLayer[]> {
  if (serviceType === 'arcgis') {
    const base = getArcgisBaseUrl(serviceUrl);
    if (!base) throw new Error('Enter a valid ArcGIS REST service URL');

    const data = await fetchJson(`${base}?f=json`, serviceUrl);
    const layers: DiscoveredLayer[] = (data?.layers ?? [])
      .filter((layer: any) => Number.isFinite(layer?.id))
      .map((layer: any) => ({
        id: String(layer.id),
        name: layer.name || `Layer ${layer.id}`,
        title: layer.name || `Layer ${layer.id}`,
        geometryType: normaliseEsriGeometry(layer.geometryType),
        arcgisLayerId: Number(layer.id),
      }));

    if (layers.length === 0) throw new Error('No layers found in this ArcGIS service');
    return layers;
  }

  // GeoServer: read WMS GetCapabilities, exactly as the Map App builder does.
  const url = new URL(serviceUrl.trim());
  url.searchParams.set('service', 'WMS');
  url.searchParams.set('request', 'GetCapabilities');
  url.searchParams.set('version', '1.3.0');

  const response = await fetch(url.toString());
  if (!response.ok) throw new Error(`GeoServer returned ${response.status}`);

  const xml = new DOMParser().parseFromString(await response.text(), 'application/xml');
  const seen = new Map<string, DiscoveredLayer>();

  for (const node of Array.from(xml.getElementsByTagName('Layer'))) {
    // Only leaf layers are requestable.
    if (node.getElementsByTagName('Layer').length > 0) continue;
    const childText = (tag: string) =>
      Array.from(node.children)
        .find((child) => child.tagName.toLowerCase() === tag)
        ?.textContent?.trim();

    const name = childText('name');
    if (!name || name.toLowerCase() === 'wms') continue;

    seen.set(name, {
      id: name,
      name,
      title: childText('title') || name,
      geometryType: 'unknown',
    });
  }

  if (seen.size === 0) throw new Error('No layers found in GetCapabilities response');
  return Array.from(seen.values());
}

function normaliseEsriGeometry(esriType: string | undefined): LayerGeometryType {
  switch (String(esriType ?? '')) {
    case 'esriGeometryPoint':
    case 'esriGeometryMultipoint':
      return 'point';
    case 'esriGeometryPolyline':
      return 'line';
    case 'esriGeometryPolygon':
    case 'esriGeometryEnvelope':
      return 'polygon';
    default:
      return 'unknown';
  }
}

/**
 * Resolves a GeoServer layer's geometry type after it is added.
 * WMS GetCapabilities does not publish it; the matching WFS endpoint does.
 */
export async function resolveGeometryType(
  source: DashboardDataSource,
): Promise<LayerGeometryType> {
  if (source.serviceType === 'local') return source.geometryType;
  if (source.serviceType === 'arcgis') return source.geometryType;
  return detectWfsGeometryType(source.serviceUrl, source.layerName);
}

/** The layer's extent in WGS84, used to frame a map widget. */
/**
 * Geometry of the features matching a filter, as GeoJSON.
 *
 * Used to HIGHLIGHT a selection on a map widget. Everything else in this file
 * asks for numbers or attribute rows; this is the one call that needs shapes,
 * which is why it is separate from `fetchRecords` rather than a flag on it.
 *
 * Capped hard: a selection that matches half a layer should draw nothing rather
 * than lock the browser up rendering it.
 */
export const HIGHLIGHT_LIMIT = 500;

export async function fetchMatchingGeometry(
  source: DashboardDataSource,
  where: string,
  /**
   * Skippable because the same call also serves attribute pairing, which needs
   * only the rows' columns. Asking a service for full geometry it will never
   * be drawn is the difference between a few KB and a few MB on a big
   * selection.
   */
  options: { includeGeometry?: boolean } = {},
): Promise<GeoJSON.FeatureCollection | null> {
  if (!where.trim()) return null;
  const includeGeometry = options.includeGeometry ?? true;

  // Answered in memory. `includeGeometry` is ignored on purpose: there is no
  // request to make smaller, and the caller reads `properties` either way.
  if (source.serviceType === 'local') {
    try {
      return localMatchingGeometry(source, where, HIGHLIGHT_LIMIT);
    } catch {
      // An expression this source cannot read highlights nothing, which is
      // what every other unmatched selection already does here.
      return null;
    }
  }

  try {
    if (source.serviceType === 'arcgis') {
      const params = new URLSearchParams({
        where: where.trim(),
        outFields: '*',
        returnGeometry: includeGeometry ? 'true' : 'false',
        outSR: '4326',
        resultRecordCount: String(HIGHLIGHT_LIMIT),
        f: 'geojson',
      });
      const data = await fetchJson(`${arcgisLayerUrl(source)}/query?${params.toString()}`, source.serviceUrl);
      return (data?.features?.length ? data : null) as GeoJSON.FeatureCollection | null;
    }

    const url = getWfsUrl(source.serviceUrl);
    url.searchParams.set('service', 'WFS');
    url.searchParams.set('version', '2.0.0');
    url.searchParams.set('request', 'GetFeature');
    url.searchParams.set('typeNames', source.layerName);
    url.searchParams.set('outputFormat', 'application/json');
    url.searchParams.set('count', String(HIGHLIGHT_LIMIT));
    url.searchParams.set('CQL_FILTER', where.trim());

    const data = await fetchJson(url.toString());
    return (data?.features?.length ? data : null) as GeoJSON.FeatureCollection | null;
  } catch (error) {
    // A highlight is an enhancement; failing it must not break the map.
    console.warn('[dashboard] could not fetch the selected geometry', error);
    return null;
  }
}

/**
 * Every geometry in a selection, merged into one shape.
 *
 * WHY NOT JUST THE FIRST ONE
 * A selection is made by VALUE, not by row: picking ward 5 can match three
 * polygons where the ward is drawn in pieces, and several rows always share a
 * value in a list or a chart slice. Filtering by the first shape alone would
 * quietly return a third of the right answer — the worst kind of wrong, since
 * the numbers still look plausible.
 *
 * MERGED, NOT UNIONED
 * The parts are collected into a Multi* geometry rather than dissolved into
 * one outline. A true union needs a geometry engine this app does not ship,
 * and INTERSECTS against the parts gives the same answer as INTERSECTS against
 * their union — anything meeting any part meets the whole.
 *
 * MIXED TYPES COLLAPSE TO THE LARGEST DIMENSION. Neither CQL nor the ArcGIS
 * query API takes a mixed GeometryCollection, so a layer holding both polygons
 * and stray points keeps the polygons: they are what carries the area someone
 * means by "inside this".
 */
export function mergeGeometry(
  collection: GeoJSON.FeatureCollection | null,
): GeoJSON.Geometry | null {
  const polygons: number[][][][] = [];
  const lines: number[][][] = [];
  const points: number[][] = [];

  for (const feature of collection?.features ?? []) {
    const geometry = feature?.geometry as any;
    switch (geometry?.type) {
      case 'Polygon': polygons.push(geometry.coordinates); break;
      case 'MultiPolygon': polygons.push(...geometry.coordinates); break;
      case 'LineString': lines.push(geometry.coordinates); break;
      case 'MultiLineString': lines.push(...geometry.coordinates); break;
      case 'Point': points.push(geometry.coordinates); break;
      case 'MultiPoint': points.push(...geometry.coordinates); break;
      default: break;
    }
  }

  if (polygons.length > 0) return { type: 'MultiPolygon', coordinates: polygons } as GeoJSON.Geometry;
  if (lines.length > 0) return { type: 'MultiLineString', coordinates: lines } as GeoJSON.Geometry;
  if (points.length > 0) return { type: 'MultiPoint', coordinates: points } as GeoJSON.Geometry;
  return null;
}

/** Bounding box of a GeoJSON collection, as [west, south, east, north]. */
export function geoJsonBounds(
  collection: GeoJSON.FeatureCollection | null,
): [number, number, number, number] | null {
  if (!collection?.features?.length) return null;

  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;

  const visit = (coordinates: unknown): void => {
    if (!Array.isArray(coordinates)) return;
    if (typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
      const [lon, lat] = coordinates as [number, number];
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
      west = Math.min(west, lon);
      east = Math.max(east, lon);
      south = Math.min(south, lat);
      north = Math.max(north, lat);
      return;
    }
    for (const part of coordinates) visit(part);
  };

  for (const feature of collection.features) {
    visit((feature.geometry as any)?.coordinates);
  }

  return Number.isFinite(west) && Number.isFinite(south) ? [west, south, east, north] : null;
}

export async function fetchSourceExtent(
  source: DashboardDataSource,
): Promise<[number, number, number, number] | null> {
  // No capabilities document to read: the features are right here.
  if (source.serviceType === 'local') return localSourceExtent(source);

  if (source.serviceType === 'arcgis') {
    try {
      const params = new URLSearchParams({
        where: '1=1',
        returnExtentOnly: 'true',
        outSR: '4326',
        f: 'json',
      });
      const data = await fetchJson(`${arcgisLayerUrl(source)}/query?${params.toString()}`, source.serviceUrl);
      return getArcgisExtentBounds(data?.extent) ?? null;
    } catch {
      return null;
    }
  }

  try {
    const url = new URL(source.serviceUrl.trim());
    url.searchParams.set('service', 'WMS');
    url.searchParams.set('request', 'GetCapabilities');
    url.searchParams.set('version', '1.3.0');

    const response = await fetch(url.toString());
    if (!response.ok) return null;

    const xml = new DOMParser().parseFromString(await response.text(), 'application/xml');
    const match = Array.from(xml.getElementsByTagName('Layer')).find((node) => {
      const name = Array.from(node.children).find(
        (child) => child.tagName.toLowerCase() === 'name',
      )?.textContent;
      return name?.trim() === source.layerName;
    });
    if (!match) return null;

    const box = Array.from(match.children).find(
      (child) => child.tagName.toLowerCase() === 'ex_geographicboundingbox',
    );
    const read = (tag: string) => Number(box?.getElementsByTagName(tag)[0]?.textContent);
    const bounds: [number, number, number, number] = [
      read('westBoundLongitude'),
      read('southBoundLatitude'),
      read('eastBoundLongitude'),
      read('northBoundLatitude'),
    ];
    return bounds.every(Number.isFinite) ? bounds : null;
  } catch {
    return null;
  }
}
